import React, { useEffect, useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ProductGrid } from '../components/Products/ProductGrid';
import { Product, getProducts, getSubCategories, SubCategory } from '../api/storeApi';

export const ProductsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentCategory, setCurrentCategory] = useState<SubCategory | null>(null);

  const searchQuery = searchParams.get('search');
  const categoryId = searchParams.get('category');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // If we have a category ID, fetch category details
        if (categoryId) {
          const categories = await getSubCategories();
          const category = categories.find((c: SubCategory) => c.id.toString() === categoryId);
          setCurrentCategory(category || null);
        } else {
          setCurrentCategory(null);
        }

        // Always fetch all products for the current category (NEVER filter by search on API)
        const data = await getProducts(categoryId || undefined);
        setProducts(data);
      } catch (err) {
        setError('Failed to load products');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);

  // Filter products in-memory using the searchQuery from the URL
  const filteredProducts = searchQuery && searchQuery.trim().length > 0
    ? products.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.brand?.name?.toLowerCase?.().includes(searchQuery.toLowerCase()) ?? false) ||
        (p.code?.toLowerCase?.().includes(searchQuery.toLowerCase()) ?? false) ||
        (p.description?.toLowerCase?.().includes(searchQuery.toLowerCase()) ?? false)
      )
    : products;

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {currentCategory && (
          <Typography variant="h4" component="h1" gutterBottom>
            {currentCategory.name}
          </Typography>
        )}
        <Box sx={{ mt: 3 }}>
          <ProductGrid
            products={filteredProducts}
            loading={loading}
            error={error}
          />
        </Box>
      </Box>
    </Container>
  );
};
